import { useState } from "react";

//  Hooks
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useGraphsFetch from "utils/hooks/useGraphsFetch";
import { useAppDispatch } from "app/hooks";

// Enums
import { urlPaths } from "enums/urlPaths";

// Utils
import { buildParamsString, forceDownload } from "utils";
import { getICReportPPT } from "services/icDashboard/icDashboard.service";
import { updateDownloadStatus } from "store/slice/UI";
import { THeaderConfigProps } from "components/Management/table-config";

type TuseHeaderActionsReturn = [
    {
        searchDataForReport: ReturnType<typeof useGraphsFetch>;
        popOverAnchor: HTMLElement | null;
    },
    {
        handleEditDomain: () => void;
        handleEditTeam: () => void;
        handleToggleReportMenu: (arg: React.MouseEvent<HTMLButtonElement> | null) => void;
        handleDownloadICReport: (isProAction: boolean, arg: React.MouseEvent<HTMLButtonElement> | null) => void;
        handleGoToVcp: () => void;
    }
];

const useHeaderActions = (selectedColumns?: THeaderConfigProps[]): TuseHeaderActionsReturn => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();

    const searchDataForReport = useGraphsFetch();

    const [popOverAnchor, setPopOverAnchor] = useState<HTMLElement | null>(null);

    const { companyId } = useParams();

    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");
    const searchAiId = searchParams.get("searchAiId");

    const params = buildParamsString({ projectId, insightId, briefId, searchAiId });
    const selectedColumnIds = selectedColumns
        ?.filter((column) => !column.isHidden && column.id !== "name-role")
        .map(({ id }) => id);

    const handleToggleReportMenu = (arg: React.MouseEvent<HTMLButtonElement> | null) => {
        setPopOverAnchor(arg === null ? arg : arg?.currentTarget);
    };

    const handleEditDomain = () => {
        navigate(`${urlPaths.Domain}/${companyId}?${params}`, {
            state: { from: location.pathname + location.search },
        });
    };

    const handleEditTeam = () => {
        navigate(`${urlPaths.Team}/${companyId}?${params}`, {
            state: { from: location.pathname + location.search },
        });
    };

    const handleGoToVcp = () => {
        navigate(`${urlPaths.Vcp}/${companyId}?${params}`, {
            state: { from: location.pathname + location.search },
        });
    };

    const handleDownloadICReport = async (isProAction: boolean, arg: React.MouseEvent<HTMLButtonElement> | null) => {
        if (isProAction) {
            setPopOverAnchor(arg === null ? arg : arg?.currentTarget);
            return;
        }

        dispatch(updateDownloadStatus({ loading: true, error: false }));
        try {
            const response = await getICReportPPT(projectId, insightId, selectedColumnIds);
            forceDownload(response, `IC_Report_${projectId}_${insightId}.pptx`);

            dispatch(updateDownloadStatus({ loading: false, error: false }));
        } catch (error) {
            console.log(error);
            dispatch(updateDownloadStatus({ loading: false, error: true }));
            setTimeout(() => {
                dispatch(updateDownloadStatus({ loading: false, error: false }));
            }, 5000);
        }
    };

    return [
        { searchDataForReport, popOverAnchor },
        {
            handleEditDomain,
            handleEditTeam,
            handleToggleReportMenu,
            handleDownloadICReport,
            handleGoToVcp,
        },
    ];
};

export default useHeaderActions;
